import { AudioSizeEnum } from 'src/app/shared/model/audio-size.enum';
import { SiaWavesurfer } from './model/sia-wavesurfer.interface';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import WaveSurfer from 'wavesurfer.js';

import { Region } from '../model/region.interface';
import { TrackCommand, TrackCommandType } from './../model/track-command.interface';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-sia-wavesurfer',
  templateUrl: './sia-wavesurfer.component.html',
  styleUrls: ['./sia-wavesurfer.component.scss']
})
export class SiaWavesurferComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dataChanges: OnChanges;
  @Input() hour = '00';
  @Input() showExtraTime = true;
  @Input() regionIn: Observable<Region>;
  @Input() clearRegion: Observable<boolean>;

  @Output() previousHour = new EventEmitter<boolean>(false);
  @Output() nextHour = new EventEmitter<boolean>(false);
  @Output() regionOut = new EventEmitter<Region>();
  @Output() clearRegions = new EventEmitter<boolean>(false);
  @Output() audioReady = new EventEmitter<boolean>(false);

  data: BehaviorSubject<string | Blob> = new BehaviorSubject<string | Blob>(null);
  eventsSubject = new Subject<TrackCommand>();
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  currentTime = '00:00';
  wavesurfer: WaveSurfer = null;
  play = false;
  showButtons: boolean;
  zoom = 0;
  start = '00:00';
  end = '59:59';
  selectDuration: string;
  totalAudioDuration: string = '59:59';
  isBackwardButtonDisabled: boolean = false;
  isForwardButtonDisabled: boolean = false;

  constructor(private changeDetector: ChangeDetectorRef) { }

  ngOnInit() {
    this.selectDuration = this.formatSecondsToDuration((+this.end) - (+this.start));
    this.regionIn?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value != null) {
        this.eventsSubject.next({ command: TrackCommandType.CHANGE_REGION, options: { start: value.start, end: value.end } });
      }
    });

    this.clearRegion?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((clear) => {
      if (clear) {
        this.eventsSubject.next({ command: TrackCommandType.CLEAR_REGION });
        this.resetValues();
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }


  async ngOnChanges(changes: SimpleChanges) {
    if (changes?.dataChanges != null) {
      const data: SiaWavesurfer = changes?.dataChanges?.currentValue;
      if (data) {
        this.showButtons = true;
        this.data.next(data.url);
        this.eventsSubject.next({ command: TrackCommandType.ADD_MARKER, options: { time: data.markerStartPosition, label: 'Start', color: '#f44336' } });
        this.eventsSubject.next({ command: TrackCommandType.ADD_MARKER, options: { time: data.markerEndPosition, label: 'End', color: '#f44336' } });
      } else {
        this.showButtons = false;
        this.data.next(null);
        this.resetValues();
      }
    }


    const currentHour = changes?.hour?.currentValue;
    if (currentHour != null) {
      this.isForwardButtonDisabled = false;
      this.isBackwardButtonDisabled = false;

      if (currentHour == 0) {
        this.isBackwardButtonDisabled = true;
      } else if (currentHour == 23) {
        this.isForwardButtonDisabled = true;
      }

    }
  }



  replayTenSeconds() {
    this.eventsSubject.next({ command: TrackCommandType.BACKWARD, options: 10 });
  }

  onPlayButtonClick() {
    this.play = true;
    this.eventsSubject.next({ command: TrackCommandType.START });
  }

  onPauseButtonClick() {
    this.play = false;
    this.eventsSubject.next({ command: TrackCommandType.PAUSE });
    this.changeDetector.detectChanges();
  }

  forwardTenSecond() {
    this.eventsSubject.next({ command: TrackCommandType.FORWARD, options: 10 });
  }

  zoomIn() {
    this.eventsSubject.next({ command: TrackCommandType.ZOOM, options: this.zoom += environment.wavesurferZoom });
  }

  zoomOut() {
    this.eventsSubject.next({
      command: TrackCommandType.ZOOM,
      options:
        this.zoom < 1
          ? (this.zoom = 0)
          : (this.zoom -= environment.wavesurferZoom)
    });
  }

  onBackwardButtonClick() {
    this.previousHour.emit(true);
    this.resetValues();
  }

  onForwardButtonClick() {
    this.nextHour.emit(true);
    this.resetValues();
  }

  clear() {
    this.eventsSubject.next({ command: TrackCommandType.CLEAR_REGION });
    this.resetValues();

    this.clearRegions.emit(true);
  }

  cut() {
    this.eventsSubject.next({ command: TrackCommandType.CUT });

  }

  paste() {
    this.eventsSubject.next({ command: TrackCommandType.PASTE });
  }

  getDuration(value) {
    if (value >= AudioSizeEnum.SIXTY_MINUTES) {
      value = AudioSizeEnum.ONE_HOUR;
    }
    this.totalAudioDuration = this.formatSecondsToDuration(value);
    this.end = this.totalAudioDuration;
    this.selectDuration = this.end;
    this.audioReady.emit(true);
  }

  getCurrentTime = (value: number): void => {
    const minutes = this.formatSecondsToDuration(value);
    this.currentTime = minutes;
    this.changeDetector.detectChanges();
  }


  public saveRegions = (regions) => {
    if (Object.keys(regions).length) {
      Object.keys(regions).forEach(id => {

        const region = regions[id];
        const regionElement = region.element;

        this.start = this.formatSecondsToDuration(this.formatTime(region.start));

        if (region.end == AudioSizeEnum.SIXTY_MINUTES) {
          region.end = AudioSizeEnum.ONE_HOUR;
        }

        this.end = this.formatSecondsToDuration(this.formatTime(region.end));

        regionElement.title = this.start + ' -> ' + this.end;

        this.regionOut.emit({ start: Math.round(region.start), end: Math.round(region.end) });

        const duration = Math.round(region.end) - Math.round(region.start);
        this.selectDuration = this.formatSecondsToDuration(duration);

      });
    } else {
      this.resetValues();
    }
  }

  // Private Methods

  private resetValues() {
    this.start = '00:00';
    this.end = this.totalAudioDuration;
    this.selectDuration = this.totalAudioDuration;
    this.currentTime = '00:00';
  }


  private formatTime(value: any) {
    let minutes: any = Math.round(value).toFixed();
    if (minutes < 10) {
      minutes = '0' + Math.round(minutes);
    }
    return minutes;
  }

  private formatSecondsToDuration(seconds: number): string {
    return moment.utc(seconds * 1000).format('mm:ss');
  }

}
